import {
	Button,
	CircularProgress,
	DialogContent,
	DialogTitle,
	Grid,
	Snackbar,
	TextField,
	makeStyles,
	IconButton,
} from "@material-ui/core";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Alert } from "@material-ui/lab";
import { useFormsContext } from "../../../../context/FormsContext";
import { ErrorModal, useAssetContext, TimeService } from "@dexteel/mesf-core";
import type { Asset } from "@dexteel/mesf-core/dist/models/Asset";
import type { FormAsset } from "../../../../models/FormAsset";
import { ScheduleCodePicker } from "../picker/ScheduleCodePicker";
import { DateTimePicker } from "@mui/x-date-pickers";
import { getMomentTz } from "@dexteel/mesf-core";
import { INITIAL_VALUES_FORM_ASSET } from "@setup/configurations/forms/models/FormAssetInitialValue";
import { TransformAssetCodeModel } from "@/utils/transforms";
import { upsertFormAsset } from "@setup/configurations/forms/repositories/FormsRepository";
import EditIcon from "@material-ui/icons/Edit";
import cronstrue from "cronstrue";

const moment = getMomentTz();

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
		"& .MuiOutlinedInput-root.Mui-disabled": {
			"& fieldset": {
				borderColor: "rgba(0, 0, 0, 0.23)",
			},
		},
	},
	unsetOutline: {
		"& .MuiOutlinedInput-root.Mui-disabled": {
			"& fieldset": {
				borderColor: "rgba(0, 0, 0, 0.23)",
			},
		},
	},
	checkbox: {
		userSelect: "none",
	},
	assetPicker: {
		"& .MuiGrid-item": {
			width: "500px",
		},
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	inputEditable: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "2px solid #3F51B5",
		},
	},
	comments: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "1px solid #3F51B5",
		},
	},
	cronField: {
		"& .MuiInputBase-root": {
			height: "40px",
		},
		"& .MuiOutlinedInput-input": {
			padding: "10px 14px",
		},
	},
	disabledDatePicker: {
		"& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
			borderWidth: "1px !important",
			borderColor: "rgba(0, 0, 0, 0.23)",
		},
		"& .MuiInputBase-input.Mui-disabled": {
			color: "rgba(0, 0, 0, 0.38)",
		},
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			borderWidth: "1px",
		},
	},
	saveButton: {
		width: "100%",
		maxWidth: "306px",
		height: "36px",
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
	dialogContentFixed: {
		minHeight: "20vh",
	},
}));

type Props = {
	FormAsset: FormAsset;
};

export const EditFormAsset = ({ FormAsset }: Props) => {
	const classes = useStyles();
	const [addSuccess, setAddSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [initialValues, setInitialValues] = useState<FormAsset | null>(null);

	const {
		AssetId,
		AssetName,
		FormAssetId,
		SchedulePolicyCode,
		Periods,
		StartDate,
		FormId,
		CronExpression: initialCronExpression,
	} = FormAsset;

	const {
		actions: { setAssetCodes, setAssetId, setAssetName, setFormAssetAssetName },
	} = useFormsContext();

	const {
		state: { allAssets },
	} = useAssetContext();

	const {
		control,
		handleSubmit,
		setValue,
		watch,
		formState: { isValid, errors },
		reset,
	} = useForm<FormAsset>({
		defaultValues: INITIAL_VALUES_FORM_ASSET,
	});

	const [cronExpression, setCronExpression] = useState<string>("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isCustomSchedule, setIsCustomSchedule] = useState(false);
	const [formHasChanged, setFormHasChanged] = useState(false);

	const currentSchedulePolicyCode = watch("SchedulePolicyCode");

	// Watch all form values to detect changes
	const formValues = watch();

	const handleCancel = async () => {
		reset();
		setAssetId(1);
		setAssetName("Plant");
	};

	const buildTreeAssets = (nodes: any) => {
		if (nodes === undefined) return;
		// Tree build functions
		const idMapping = nodes.reduce((acc: any, el: any, i: number) => {
			acc[el.id] = i;
			return acc;
		}, {});
		let root;
		nodes.forEach((el: any) => {
			if (el.parentId === null) {
				root = el;
				return;
			}
			const parentEl = nodes[idMapping[el.parentId]];
			if (parentEl) {
				parentEl.children = [...(parentEl?.children || []), el];
			}
		});
		setAssetCodes(root as any);
	};

	const loadFilterAssets = () => {
		let mydataSource: any[] = [];
		allAssets.forEach((row: Asset) => {
			const code = TransformAssetCodeModel(row);
			mydataSource = [...mydataSource, code];
		});
		buildTreeAssets(mydataSource);
	};

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setAddSuccess(false);
	};

	const onSubmit = async (data: FormAsset) => {
		setIsSubmitLoading(true);
		const submitData = {
			...data,
			CronExpression: cronExpression || null,
		};

		const res = await upsertFormAsset(submitData);
		if (res.ok) {
			setIsSubmitLoading(false);
			setAddSuccess(true);

			// Update initial values after successful save
			setInitialValues({
				...submitData,
				CronExpression: cronExpression,
			});

			// Reset form change state
			setFormHasChanged(false);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	const getAssetName = () => {
		setFormAssetAssetName(
			allAssets.find((asset) => asset.AssetId === AssetId)?.AssetName ||
				"Unknown",
		);
	};

	const handleScheduleStartChange = (date: Date) => {
		setValue("StartDate", date);
	};

	// Check if schedule policy changed
	useEffect(() => {
		setIsCustomSchedule(currentSchedulePolicyCode === "P");
	}, [currentSchedulePolicyCode]);

	// Check if any form value has changed
	useEffect(() => {
		if (initialValues) {
			const hasChangedCron = initialCronExpression !== cronExpression;
			const hasChangedFields =
				initialValues.SchedulePolicyCode !== formValues.SchedulePolicyCode ||
				initialValues.Periods !== formValues.Periods ||
				initialValues.StartDate?.toString() !==
					formValues.StartDate?.toString();

			setFormHasChanged(hasChangedCron || hasChangedFields);
		}
	}, [formValues, cronExpression, initialValues]);

	useEffect(() => {
		if (FormAssetId) {
			handleCancel();
			reset();
			loadFilterAssets();
			setFormAssetAssetName(AssetId);
			getAssetName();
			setValue("AssetId", AssetId);
			setValue("SchedulePolicyCode", SchedulePolicyCode);
			setValue("Periods", Periods);
			setValue("StartDate", StartDate);
			setValue("FormId", FormId);
			setValue("FormAssetId", FormAssetId);
			setIsSubmitLoading(false);

			// Set cronExpression if it exists
			if (initialCronExpression) {
				setCronExpression(initialCronExpression);
			}

			// Update isCustomSchedule based on SchedulePolicyCode
			setIsCustomSchedule(SchedulePolicyCode === "P");

			// Store initial values for change detection
			setInitialValues({
				AssetId,
				AssetName,
				FormAssetId,
				SchedulePolicyCode,
				Periods,
				StartDate,
				FormId,
				CronExpression: initialCronExpression,
			});
		}
	}, []);

	return (
		<>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Grid container>
					<DialogTitle
						style={{
							padding: "0 5px",
							marginRight: 20,
							width: "100%",
						}}
					>
						<Grid item md={12} xs={12}>
							<h1 style={{ fontSize: "1em" }}>Asset: {AssetName}</h1>
						</Grid>
					</DialogTitle>
					<DialogContent
						dividers
						style={{ padding: "8px" }}
						className={classes.dialogContentFixed}
					>
						<Grid container>
							<Grid item md={4} xs={12} className={classes.inputEditable}>
								<Controller
									name="SchedulePolicyCode"
									control={control}
									render={({ field: { onChange, value } }) => (
										<ScheduleCodePicker
											value={value as string}
											onChange={onChange}
											cronExpression={cronExpression}
											onCronExpressionChange={setCronExpression}
											isModalOpen={isModalOpen}
											setIsModalOpen={setIsModalOpen}
											hideScheduleStart={setIsCustomSchedule}
											onScheduleStartChange={handleScheduleStartChange}
											scheduleStart={watch("StartDate")}
										/>
									)}
								/>
							</Grid>
							<Grid
								item
								md={2}
								xs={2}
								style={{ paddingLeft: 10 }}
								className={classes.inputEditable}
							>
								<Controller
									name="Periods"
									control={control}
									rules={{ required: true, pattern: /^[0-9]*$/ }}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											className={classes.root}
											style={{ margin: "8px 0" }}
											size="small"
											label="Periods"
											multiline
											variant="outlined"
											fullWidth
											autoComplete="off"
											value={value}
											onChange={(evt) => {
												const newValue = evt.target.value.replace(
													/[^0-9]/g,
													"",
												);
												if (newValue.length <= 99) onChange(newValue);
											}}
											error={!!error}
											helperText={error ? "Enter a period number" : ""}
										/>
									)}
								/>
							</Grid>

							<Grid
								container
								item
								md={6}
								xs={12}
								className={classes.inputEditable}
								style={{ paddingLeft: "22px" }}
							>
								<Controller
									name="StartDate"
									control={control}
									render={({ field }) => (
										<DateTimePicker
											label="Start Date"
											timezone={
												isCustomSchedule
													? "UTC"
													: TimeService.getInstance().getServerTimeZone()
											}
											format={
												isCustomSchedule ? "MM/DD/YYYY" : "MM/DD/YYYY HH:mm"
											}
											timeSteps={{ minutes: 1 }}
											value={moment(field.value) as any}
											onChange={field.onChange}
											inputRef={field.ref}
											disabled={isCustomSchedule}
											sx={{
												width: "100%",
												"& .MuiFormLabel-root": {
													marginTop: "5px !important",
												},
												"& .MuiButtonBase-root": {
													marginTop: "8px !important",
												},
											}}
											slotProps={{
												textField: {
													fullWidth: true,
													variant: "outlined",
													error: !!errors.StartDate,
													className: classes.disabledDatePicker,
													InputProps: {
														style: {
															height: "40px",
															marginTop: 8.5,
														},
													},
													inputProps: {
														style: {
															paddingTop: "16px",
														},
													},
												},
											}}
										/>
									)}
								/>
								{errors.StartDate && (
									<span className={classes.errorLabel}>
										{errors.StartDate.message}
									</span>
								)}
							</Grid>

							{isCustomSchedule && cronExpression && (
								<Grid
									item
									md={12}
									xs={12}
									className={classes.inputEditable}
									style={{ marginTop: "8px" }}
								>
									<TextField
										label="Schedule"
										variant="outlined"
										fullWidth
										className={classes.cronField}
										value={cronstrue.toString(cronExpression)}
										InputProps={{
											readOnly: true,
											endAdornment: (
												<IconButton
													edge="end"
													onClick={() => setIsModalOpen(true)}
													size="small"
												>
													<EditIcon />
												</IconButton>
											),
										}}
									/>
								</Grid>
							)}

							<Grid
								container
								item
								style={{
									display: "flex",
									justifyContent: "flex-end",
									marginTop: 12,
									marginBottom: 8,
									width: "100%",
								}}
							>
								<Button
									className={classes.saveButton}
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={
										!isValid ||
										isSubmitLoading ||
										(currentSchedulePolicyCode === "P" &&
											(!cronExpression ||
												cronExpression.trim().length === 0)) ||
										!formHasChanged
									}
									variant="contained"
									color="primary"
									type="submit"
								>
									SAVE
								</Button>
							</Grid>
						</Grid>
					</DialogContent>
				</Grid>
			</form>
			<Snackbar open={addSuccess} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
